import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { translations } from "@/i18n/translations";
import { FileText, Plus, Trash2, History, Upload, Copy, Search } from "lucide-react";

const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

/* ------------------------- PAGES ------------------------- */
export const PagesSection = () => {
  const [pages, setPages] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPage, setNewPage] = useState({ slug: "", title_en: "" });

  const load = () => adminApi.listPages().then(setPages);
  useEffect(() => { load(); }, []);
  const open = (p) => { setSel(p.slug); setForm({ ...p }); setShowVersions(false); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      await adminApi.updatePage(sel, form);
      toast.success("Page saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const create = async () => {
    try {
      const p = await adminApi.createPage(newPage);
      toast.success("Page created");
      setCreating(false); setNewPage({ slug: "", title_en: "" });
      await load(); open(p);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const del = async (slug) => {
    if (!window.confirm("Delete this page?")) return;
    try { await adminApi.deletePage(slug); toast.success("Deleted"); setSel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const loadVersions = async () => { setVersions(await adminApi.pageVersions(sel)); setShowVersions(true); };
  const restore = async (vid) => {
    const p = await adminApi.restorePage(sel, vid); setForm({ ...p }); toast.success("Restored"); setShowVersions(false); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Pages</h2>
        <Button onClick={() => setCreating((c) => !c)} data-testid="page-new-btn" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> New page</Button>
      </div>

      {creating && (
        <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <Field label="Slug (URL, e.g. events)" value={newPage.slug} onChange={(v) => setNewPage((n) => ({ ...n, slug: v }))} testid="page-new-slug" />
          <Field label="Title (English)" value={newPage.title_en} onChange={(v) => setNewPage((n) => ({ ...n, title_en: v }))} testid="page-new-title" />
          <p className="text-xs text-gmoaa-text-muted">The new page will be available at <b>/p/your-slug</b>. Add it to the menu from Navigation.</p>
          <Button onClick={create} data-testid="page-new-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Create</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-1.5">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => open(p)} data-testid={`page-item-${p.slug}`}
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${sel === p.slug ? "border-gmoaa-green bg-gmoaa-green-light" : "border-gmoaa-border-subtle bg-white hover:bg-gmoaa-surface"}`}>
              <span className="flex items-center gap-2"><FileText size={14} /> {p.title_en}</span>
              <span className={`text-[10px] ${p.published ? "text-gmoaa-green" : "text-gmoaa-text-muted"}`}>{p.published ? "Live" : "Draft"}</span>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {sel ? (
            <div className="space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gmoaa-text-muted">/{form.system ? sel : `p/${sel}`}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={loadVersions} data-testid="page-versions-btn"><History size={14} className="mr-1" /> History</Button>
                  {!form.system && <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => del(sel)} data-testid="page-delete-btn"><Trash2 size={14} /></Button>}
                </div>
              </div>
              {showVersions ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Previous versions</p>
                  {versions.length === 0 && <p className="text-xs text-gmoaa-text-muted">No earlier versions yet.</p>}
                  {versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between rounded border border-gmoaa-border-subtle p-2 text-xs">
                      <span>{new Date(v.at).toLocaleString()} · {v.actor}</span>
                      <Button size="sm" variant="outline" onClick={() => restore(v.id)} data-testid={`page-restore-${v.id}`}>Restore</Button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setShowVersions(false)}>Close</Button>
                </div>
              ) : (
                <>
                  <Field label="Title (English)" value={form.title_en} onChange={set("title_en")} testid="page-title-en" />
                  <Field label="Title (Gujarati)" value={form.title_gu} onChange={set("title_gu")} testid="page-title-gu" />
                  <Field label="Body — English (HTML allowed)" textarea value={form.body_en} onChange={set("body_en")} testid="page-body-en" />
                  <Field label="Body — Gujarati (HTML allowed)" textarea value={form.body_gu} onChange={set("body_gu")} testid="page-body-gu" />
                  <Field label="SEO title" value={form.seo_title} onChange={set("seo_title")} testid="page-seo-title" />
                  <Field label="SEO description" textarea value={form.seo_desc} onChange={set("seo_desc")} testid="page-seo-desc" />
                  {sel === "constitution" && <Field label="PDF document URL (upload in Media, paste URL)" value={form.pdf_url} onChange={set("pdf_url")} testid="page-pdf-url" />}
                  <div className="flex items-center gap-2">
                    <Switch checked={!!form.published} onCheckedChange={set("published")} data-testid="page-published" />
                    <Label className="text-sm">Published (visible on the site)</Label>
                  </div>
                  <Button onClick={save} data-testid="page-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save page</Button>
                </>
              )}
            </div>
          ) : <p className="text-sm text-gmoaa-text-muted">Select a page to edit, or create a new one.</p>}
        </div>
      </div>
    </div>
  );
};

/* ------------------------- LABELS ------------------------- */
export const LabelsSection = () => {
  const [overrides, setOverrides] = useState({});
  const [q, setQ] = useState("");
  const keys = Object.keys(translations.en);

  useEffect(() => { adminApi.getLabels().then((d) => setOverrides(d || {})); }, []);

  const val = (k, lng) => {
    const o = overrides[k];
    if (o && o[lng] != null && o[lng] !== "") return o[lng];
    return translations[lng][k] ?? "";
  };
  const edit = (k, lng) => (e) => {
    const v = e.target.value;
    setOverrides((o) => ({ ...o, [k]: { en: lng === "en" ? v : (o[k]?.en ?? translations.en[k] ?? ""), gu: lng === "gu" ? v : (o[k]?.gu ?? translations.gu[k] ?? "") } }));
  };
  const save = async () => {
    try { await adminApi.putLabels(overrides); toast.success("Labels saved — reload the site to see changes"); }
    catch { toast.error("Save failed"); }
  };
  const filtered = keys.filter((k) => !q || k.toLowerCase().includes(q.toLowerCase()) || (translations.en[k] || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Text & translations</h2>
        <Button onClick={save} data-testid="labels-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save all</Button>
      </div>
      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gmoaa-text-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search labels…" className="pl-9" data-testid="labels-search" />
      </div>
      <div className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white">
        <div className="grid grid-cols-12 gap-2 border-b border-gmoaa-border-subtle bg-gmoaa-green-light px-3 py-2 text-xs font-semibold">
          <div className="col-span-2">Key</div><div className="col-span-5">English</div><div className="col-span-5">ગુજરાતી</div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {filtered.map((k) => (
            <div key={k} className="grid grid-cols-12 items-center gap-2 border-b border-gmoaa-border-subtle px-3 py-1.5">
              <div className="col-span-2 truncate text-xs text-gmoaa-text-muted" title={k}>{k}</div>
              <div className="col-span-5"><Input value={val(k, "en")} onChange={edit(k, "en")} data-testid={`label-${k}-en`} className="h-8 text-sm" /></div>
              <div className="col-span-5"><Input value={val(k, "gu")} onChange={edit(k, "gu")} data-testid={`label-${k}-gu`} className="h-8 text-sm" /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------- MEDIA ------------------------- */
export const MediaSection = () => {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = () => adminApi.listMedia().then(setMedia);
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try { await adminApi.uploadMedia(file); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const del = async (id) => { if (!window.confirm("Delete this file?")) return; await adminApi.deleteMedia(id); toast.success("Deleted"); load(); };
  const copy = (url) => { navigator.clipboard.writeText(mediaUrl(url)); toast.success("URL copied"); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Media library</h2>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gmoaa-green px-3 py-2 text-sm font-medium text-white hover:bg-gmoaa-green-hover">
          <Upload size={15} /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={onUpload} disabled={uploading} data-testid="media-upload-input" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {media.map((m) => (
          <div key={m.id} className="rounded-xl border border-gmoaa-border-subtle bg-white p-2" data-testid={`media-${m.id}`}>
            <div className="flex h-28 items-center justify-center overflow-hidden rounded bg-gmoaa-surface">
              {m.content_type?.startsWith("image/")
                ? <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" />
                : <FileText size={34} className="text-gmoaa-text-muted" />}
            </div>
            <p className="mt-1.5 truncate text-xs text-gmoaa-text-dark" title={m.filename}>{m.filename || "file"}</p>
            <div className="mt-1 flex items-center justify-between">
              <button onClick={() => copy(m.url)} className="text-xs text-gmoaa-green hover:underline" data-testid={`media-copy-${m.id}`}><Copy size={12} className="mr-0.5 inline" /> URL</button>
              <button onClick={() => del(m.id)} className="text-gmoaa-red" data-testid={`media-del-${m.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {media.length === 0 && <p className="text-sm text-gmoaa-text-muted">No media yet. Upload images or PDFs to reuse across pages.</p>}
      </div>
    </div>
  );
};
